import React, { useEffect, useRef, useState } from 'react';
import {
    stopInteractiveSession,
    enablePickMode, enablePickModeSilent,
    disablePickMode, disablePickModeSilent,
    getPickResult, captureElementAtCursor, injectMouseTracker,
    triggerCapture, freezeSession, unfreezeSession,
} from '../services/interactiveService';

interface InteractiveBrowserProps {
    sessionId: string;
    vncUrl: string;
    onClose: () => void;
    onElementPicked?: (locator: { method: string; value: string }, frameIndex?: number, isNewWindow?: boolean, frameName?: string) => void;
    pickModeRequested?: boolean;
    pickMulti?: boolean;   // ← new
    onPickModeChange?: (active: boolean) => void;
    onRestoreRef?: (restoreFn: () => void) => void;
    parentLocator?: { method: string; value: string } | null;
}

const DEFAULT_W = Math.min(1100, window.innerWidth * 0.8);
const DEFAULT_H = Math.min(window.innerHeight * 0.78, 700);
const DEFAULT_X = Math.round((window.innerWidth - DEFAULT_W) / 2);
const DEFAULT_Y = Math.round((window.innerHeight - DEFAULT_H) / 2);

const InteractiveBrowser: React.FC<InteractiveBrowserProps> = ({
    sessionId,
    vncUrl,
    onClose,
    onElementPicked,
    pickModeRequested,
    pickMulti,
    onPickModeChange,
    onRestoreRef,
    parentLocator,
}) => {
    const [pickMode, setPickMode] = useState(false);
    const [silentPickMode, setSilentPickMode] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [frozen, setFrozen] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const pollRef = useRef<NodeJS.Timeout | null>(null);

    const [pos, setPos] = useState({ x: DEFAULT_X, y: DEFAULT_Y });
    const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });

    const posRef = useRef(pos);
    const sizeRef = useRef(size);
    useEffect(() => { posRef.current = pos; }, [pos]);
    useEffect(() => { sizeRef.current = size; }, [size]);

    const pinnedRef = useRef(pinned);
    useEffect(() => { pinnedRef.current = pinned; }, [pinned]);

    const silentPickModeRef = useRef(silentPickMode);
    useEffect(() => { silentPickModeRef.current = silentPickMode; }, [silentPickMode]);

    const pickModeRef = useRef(pickMode);
    useEffect(() => { pickModeRef.current = pickMode; }, [pickMode]);

    useEffect(() => {
        onRestoreRef?.(() => setMinimized(false));
    }, [onRestoreRef]);

    useEffect(() => {
        if (pickModeRequested && !pickMode) handleEnablePickMode();
    }, [pickModeRequested]);

    const onHeaderMouseDown = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).tagName === 'BUTTON') return;
        e.preventDefault();
        const startX = e.clientX;
        const startY = e.clientY;
        const origX = posRef.current.x;
        const origY = posRef.current.y;
        const onMove = (ev: MouseEvent) => {
            setPos({ x: Math.max(0, origX + ev.clientX - startX), y: Math.max(0, origY + ev.clientY - startY) });
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const onResizeMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startY = e.clientY;
        const origW = sizeRef.current.w;
        const origH = sizeRef.current.h;
        const onMove = (ev: MouseEvent) => {
            setSize({ w: Math.max(400, origW + ev.clientX - startX), h: Math.max(300, origH + ev.clientY - startY) });
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const handleEnablePickMode = async () => {
        try {
            setMinimized(false);
            if (silentPickMode) {
                await injectMouseTracker(sessionId);
                await enablePickModeSilent(sessionId, parentLocator);
            } else {
                await enablePickMode(sessionId, parentLocator, pickMulti);
            }
            setPickMode(true);
            onPickModeChange?.(true);
            startPollingForResult();
        } catch (err) {
            console.error('Failed to enable pick mode:', err);
        }
    };

    const handleCancelPickMode = async () => {
        try {
            if (silentPickMode) {
                await disablePickModeSilent(sessionId);
            } else {
                await disablePickMode(sessionId);
            }
            setPickMode(false);
            onPickModeChange?.(false);
            stopPollingForResult();
        } catch (err) {
            console.error('Failed to disable pick mode:', err);
        }
    };

    const stopPollingForResult = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const startPollingForResult = () => {
        stopPollingForResult();
        pollRef.current = setInterval(async () => {
            try {
                const result = await getPickResult(sessionId);
                if (result) {
                    // Cleanup ALL overlays in ALL windows/frames before calling back
                    try {
                        if (silentPickModeRef.current) {
                            await disablePickModeSilent(sessionId);
                        } else {
                            await disablePickMode(sessionId);
                        }
                    } catch (err) {}

                    onElementPicked?.(result.selector, result.frameIndex, result.isNewWindow, result.frameName);
                    setPickMode(false);
                    onPickModeChange?.(false);
                    stopPollingForResult();
                    if (!pinnedRef.current) {
                        setMinimized(true);
                    }
                }
            } catch (err) {}
        }, 500);
    };

    useEffect(() => { return () => stopPollingForResult(); }, []);

    const handleClose = async () => {
        stopPollingForResult();
        if (frozen) { try { await unfreezeSession(sessionId); } catch {} }
        try { await stopInteractiveSession(sessionId); } catch (err) {}
        onClose();
    };

    const handleToggleFreeze = async () => {
        try {
            if (frozen) {
                await unfreezeSession(sessionId);
                setFrozen(false);
            } else {
                await freezeSession(sessionId);
                setFrozen(true);
            }
        } catch (err) {
            console.error('Freeze toggle failed:', err);
        }
    };

    const handleCaptureNow = async () => {
        const result = await captureElementAtCursor(sessionId);
        if (result) {
            try { await disablePickModeSilent(sessionId); } catch (err) {}
            onElementPicked?.(result.selector, result.frameIndex, result.isNewWindow, result.frameName);
            setPickMode(false);
            onPickModeChange?.(false);
            stopPollingForResult();
            if (!pinnedRef.current) setMinimized(true);
        } else {
            alert('No element found at cursor. Move mouse over the target element first.');
        }
    };

    useEffect(() => {
        const onKeyDown = async (e: KeyboardEvent) => {
            if (e.altKey && e.key === 'c' && silentPickModeRef.current && pickModeRef.current) {
                e.preventDefault();
                const result = await triggerCapture(sessionId);
                if (result) {
                    try { await disablePickModeSilent(sessionId); } catch (err) {}
                    onElementPicked?.(result.selector, result.frameIndex, result.isNewWindow, result.frameName);
                    setPickMode(false);
                    onPickModeChange?.(false);
                    stopPollingForResult();
                    if (!pinnedRef.current) setMinimized(true);
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [sessionId]);

    if (minimized) {
        return (
            <div className="interactive-minimized" onClick={() => setMinimized(false)}>
                🌍 Interactive Browser {pickMode && '🎯'} {frozen && '❄️'}
                <span className="interactive-minimized-hint">Click to restore</span>
            </div>
        );
    }

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, pointerEvents: 'none' }}>
            <div
                style={{ position: 'absolute', inset: 0, background: pinned ? 'transparent' : 'rgba(0,0,0,0.45)', pointerEvents: pinned ? 'none' : 'auto' }}
                onClick={() => { if (!pinned) setMinimized(true); }}
            />
            <div
                style={{
                    position: 'absolute',
                    left: pos.x,
                    top: pos.y,
                    width: size.w,
                    height: size.h,
                    background: '#1e1e1e',
                    borderRadius: 8,
                    boxShadow: pinned ? '0 8px 32px rgba(0,122,204,0.4)' : '0 8px 32px rgba(0,0,0,0.5)',
                    border: pinned ? '2px solid #007acc' : '2px solid transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    pointerEvents: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    onMouseDown={onHeaderMouseDown}
                    style={{
                        padding: '10px 16px',
                        background: '#333',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid #555',
                        flexShrink: 0,
                        cursor: 'move',
                        userSelect: 'none',
                    }}
                >
                    <h3 style={{ margin: 0, color: 'white', fontSize: 14, pointerEvents: 'none' }}>
                        🌍 Interactive Browser {pickMode && <span style={{ color: '#FF9800' }}>— 🎯 Pick Mode Active</span>}
                        {frozen && <span style={{ color: '#E91E63' }}> — ❄️ Frozen</span>}
                        <span style={{ fontSize: 10, color: '#aaa', marginLeft: 8 }}>drag to move</span>
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {pickMode && (
                            <button className="btn-pick active" onClick={handleCancelPickMode}>✋ Cancel Pick</button>
                        )}
                        <button
                            onClick={() => setSilentPickMode(s => !s)}
                            title={silentPickMode ? 'Silent Pick ON. Click to switch to normal pick.' : 'Normal Pick. Click to switch to Silent Pick.'}
                            style={{ padding: '5px 12px', background: silentPickMode ? '#9C27B0' : '#555', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}
                        >
                            {silentPickMode ? '🤫 Silent Pick' : '🎯 Normal Pick'}
                        </button>
                        <button
                            onClick={() => setPinned(p => !p)}
                            title={pinned ? 'Unpin' : 'Pin — prevent minimize when clicking outside'}
                            style={{ padding: '5px 12px', background: pinned ? '#007acc' : '#555', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}
                        >
                            {pinned ? '📌 Pinned' : '📌 Pin'}
                        </button>
                        <button
                            onClick={handleToggleFreeze}
                            title={frozen ? 'Unfreeze — resume JavaScript execution' : 'Freeze — pause all JavaScript execution to capture transient elements'}
                            style={{ padding: '5px 12px', background: frozen ? '#E91E63' : '#555', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}
                        >
                            {frozen ? '❄️ Frozen' : '🧊 Freeze'}
                        </button>
                        <button className="btn-minimize-interactive" onClick={() => setMinimized(true)}>➖ Minimize</button>
                        <button className="btn-close-interactive" onClick={handleClose}>✕ Close</button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#000', position: 'relative' }}>
                    {pickMode && (
                        <div className="pick-mode-banner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                            {silentPickMode ? (
                                <span>🤫 Silent Pick — hover over element in browser, then press <kbd style={{ background: 'rgba(255,255,255,0.3)', padding: '1px 6px', borderRadius: 3, fontWeight: 'bold' }}>Alt+C</kbd> to capture</span>
                            ) : (
                                <span>🎯 Pick Mode Active — Click on an element in the browser to select it</span>
                            )}
                        </div>
                    )}
                    {!iframeLoaded && (
                        <div className="interactive-browser-loader">
                            <div className="interactive-loader-spinner"></div>
                            <p>Loading browser session...</p>
                        </div>
                    )}
                    <iframe
                        src={vncUrl}
                        title="Interactive Browser"
                        allow="clipboard-read; clipboard-write"
                        onLoad={() => setIframeLoaded(true)}
                        style={{ display: iframeLoaded ? 'block' : 'none', flex: 1, width: '100%', height: '100%', border: 'none' }}
                    />
                </div>

                {/* Resize grip */}
                <div
                    onMouseDown={onResizeMouseDown}
                    title="Drag to resize"
                    style={{ position: 'absolute', bottom: 0, right: 0, width: 20, height: 20, cursor: 'nwse-resize', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <svg width="12" height="12" viewBox="0 0 12 12">
                        <path d="M10 2L2 10M6 2L2 6M10 6L6 10" stroke="#aaa" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default InteractiveBrowser;